import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtDate, fmtMoney } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Filter, ArrowRight, AlertTriangle, Calendar as CalendarIcon, TrendingUp, Users as UsersIcon, Sparkles, Pencil, Trash2, Upload, CheckCircle2, Megaphone, XCircle, ChevronDown, ChevronUp, Inbox, Search, X, Coffee } from "lucide-react";
import { toast } from "sonner";
import LeadsSummary from "@/components/LeadsSummary";
import InboundLeadsQueue from "@/components/InboundLeadsQueue";
import LeadsActivityCalendar from "@/components/LeadsActivityCalendar";
import ProductCheckboxes from "@/components/ProductCheckboxes";
import SendLeadsSummaryDialog from "@/components/SendLeadsSummaryDialog";
import { AccountPicker } from "@/components/AccountPicker";

const STATUS_META = {
  Unqualified: { color: "bg-slate-100 text-slate-700 border-slate-200" },
  MQL: { color: "bg-blue-100 text-blue-800 border-blue-200" },
  SQL: { color: "bg-violet-100 text-violet-800 border-violet-200" },
  Converted: { color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  Nurture: { color: "bg-amber-100 text-amber-800 border-amber-200" },
  Dead: { color: "bg-rose-100 text-rose-800 border-rose-200" },
};

const PERIODS = [
  { v: "current_month", l: "Current month" },
  { v: "current_q", l: "Current quarter" },
  { v: "last_q", l: "Last quarter" },
  { v: "last_30", l: "Last 30 days" },
  { v: "last_90", l: "Last 90 days" },
  { v: "all", l: "All time" },
];

export default function Leads() {
  const { user } = useAuth();
  const [meta, setMeta] = useState({ statuses: [], industries: [], next_actions: [] });
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterOwner, setFilterOwner] = useState("all");
  const [period, setPeriod] = useState("current_q");
  const [loading, setLoading] = useState(true);

  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [converting, setConverting] = useState(null);
  const [killing, setKilling] = useState(null);
  const [nurturing, setNurturing] = useState(null);
  const [queueCount, setQueueCount] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);
  const [tab, setTab] = useState("active"); // active | converted
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus !== "all") params.status = filterStatus;
      if (filterOwner !== "all") params.owner_id = filterOwner;
      const [l, m, u, d, q] = await Promise.all([
        api.get("/leads", { params }),
        api.get("/leads/meta"),
        api.get("/users").catch(() => ({ data: [] })),
        api.get(`/leads/dashboard?period=${period}${filterOwner !== "all" ? `&owner_id=${filterOwner}` : ""}`),
        api.get("/leads/inbound-queue").catch(() => ({ data: { count: 0 } })),
      ]);
      setLeads(l.data);
      setMeta(m.data);
      setUsers(u.data);
      setDashboard(d.data);
      setQueueCount(q.data.count || 0);
    } finally {
      setLoading(false);
      setRefreshKey((k) => k + 1);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterStatus, filterOwner, period]);

  const ownerOptions = useMemo(() => [{ user_id: "all", name: "All owners" }, ...(users || [])], [users]);

  const reviveLead = async (lead) => {
    try {
      await api.post(`/leads/${lead.id}/revive`);
      toast.success("Lead revived to the active pipeline");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to revive");
    }
  };

  const activeLeads = useMemo(() => leads.filter((l) => l.status !== "Converted" && l.status !== "Dead" && l.status !== "Nurture"), [leads]);
  const convertedLeads = useMemo(() => leads.filter((l) => l.status === "Converted"), [leads]);
  const deadLeads = useMemo(() => leads.filter((l) => l.status === "Dead"), [leads]);
  const nurtureLeads = useMemo(() => leads.filter((l) => l.status === "Nurture"), [leads]);
  const baseLeads = tab === "converted" ? convertedLeads : tab === "dead" ? deadLeads : tab === "nurture" ? nurtureLeads : activeLeads;
  const visibleLeads = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return baseLeads;
    return baseLeads.filter((l) => {
      const fullName = `${l.first_name || ""} ${l.last_name || ""}`.toLowerCase();
      return (
        fullName.includes(q) ||
        (l.company || "").toLowerCase().includes(q) ||
        (l.job_title || "").toLowerCase().includes(q) ||
        (l.email || "").toLowerCase().includes(q)
      );
    });
  }, [baseLeads, search]);

  return (
    <div className="space-y-6" data-testid="leads-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-700">Pipeline</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Leads</h1>
          <p className="text-slate-500 text-sm mt-1 max-w-2xl">
            Capture early-stage prospects. Qualify through MQL → SQL → Converted. Converting creates an Account and a live Deal.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-44" data-testid="period-select"><SelectValue /></SelectTrigger>
            <SelectContent>{PERIODS.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filterOwner} onValueChange={setFilterOwner}>
            <SelectTrigger className="w-44" data-testid="owner-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ownerOptions.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={() => setShowBulkImport(true)} data-testid="bulk-import-leads-btn">
            <Upload className="h-4 w-4 mr-1.5" /> Bulk import
          </Button>
          <SendLeadsSummaryDialog />
          <Button onClick={() => setShowCreate(true)} data-testid="new-lead-btn">
            <Plus className="h-4 w-4 mr-1.5" /> New lead
          </Button>
        </div>
      </div>

      {/* Dashboard tiles */}
      {dashboard && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <KpiTile label={`Converted (${PERIODS.find((p) => p.v === period)?.l.toLowerCase()})`} value={dashboard.converted_in_period} icon={Sparkles} accent="emerald" />
          <KpiTile label="ML (Marketing Leads)" value={dashboard.hubspot_count || 0} icon={Megaphone} accent="orange" />
          <KpiTile label="MQL" value={dashboard.status_spread?.MQL || 0} icon={TrendingUp} accent="blue" />
          <KpiTile
            label={`SQL (${PERIODS.find((p) => p.v === period)?.l.toLowerCase()})`}
            value={dashboard.sql_in_period ?? dashboard.status_spread?.SQL ?? 0}
            icon={TrendingUp}
            accent="violet"
            hint="Includes converted — total SQL-quality leads generated in this period"
          />
          <KpiTile label="Stale (>3 months)" value={dashboard.stale_leads?.length || 0} icon={AlertTriangle} accent="amber" />
        </div>
      )}

      {/* Leads generated summary (period + owner toggle, expandable drilldown) */}
      <LeadsSummary users={users} refreshKey={refreshKey} />

      {/* Tab switcher: Active | Converted | Dead | HubSpot Queue */}
      <div className="flex items-center gap-1 border-b border-slate-200 flex-wrap" data-testid="leads-tabs">
        <button
          type="button"
          onClick={() => { setTab("active"); setFilterStatus("all"); }}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === "active" ? "border-blue-600 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
          data-testid="leads-tab-active"
        >
          Active <span className="ml-1 text-[11px] font-mono text-slate-400">({activeLeads.length})</span>
        </button>
        <button
          type="button"
          onClick={() => { setTab("converted"); setFilterStatus("all"); }}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${tab === "converted" ? "border-emerald-600 text-emerald-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
          data-testid="leads-tab-converted"
        >
          <CheckCircle2 className="h-3.5 w-3.5" /> Converted <span className="text-[11px] font-mono text-slate-400">({convertedLeads.length})</span>
        </button>
        <button
          type="button"
          onClick={() => { setTab("nurture"); setFilterStatus("all"); }}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${tab === "nurture" ? "border-amber-600 text-amber-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
          data-testid="leads-tab-nurture"
        >
          <Coffee className="h-3.5 w-3.5" /> Nurture <span className="text-[11px] font-mono text-slate-400">({nurtureLeads.length})</span>
        </button>
        <button
          type="button"
          onClick={() => { setTab("dead"); setFilterStatus("all"); }}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${tab === "dead" ? "border-rose-600 text-rose-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
          data-testid="leads-tab-dead"
        >
          <XCircle className="h-3.5 w-3.5" /> Dead <span className="text-[11px] font-mono text-slate-400">({deadLeads.length})</span>
        </button>
        <button
          type="button"
          onClick={() => { setTab("hubspot"); setFilterStatus("all"); }}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${tab === "hubspot" ? "border-orange-600 text-orange-700" : "border-transparent text-slate-500 hover:text-slate-700"}`}
          data-testid="leads-tab-hubspot"
        >
          <Inbox className="h-3.5 w-3.5" /> HubSpot Queue <span className={`text-[11px] font-mono ${queueCount > 0 ? "text-orange-600 font-semibold" : "text-slate-400"}`}>({queueCount})</span>
        </button>
      </div>

      {/* HubSpot inbound queue lives in its own tab now */}
      {tab === "hubspot" && (
        <InboundLeadsQueue
          users={users}
          meta={meta}
          onChange={load}
          countHint={queueCount}
        />
      )}

      {/* Filter row — only status filters for Active tab; Converted tab is a single flat list */}
      {tab === "active" && (
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-3 flex-wrap">
            <span className="text-[10px] font-mono uppercase tracking-wider text-slate-500 flex items-center gap-1.5">
              <Filter className="h-3 w-3" /> Filter
            </span>
            {["all", ...meta.statuses.filter((s) => s !== "Converted" && s !== "Dead" && s !== "Nurture")].map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setFilterStatus(s)}
                className={`text-xs px-2.5 py-1 rounded border transition-colors ${filterStatus === s ? "bg-blue-100 border-blue-300 text-blue-900 font-semibold" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"}`}
                data-testid={`filter-${s}`}
              >
                {s === "all" ? "All" : s}
              </button>
            ))}
            <span className="text-xs text-slate-500 ml-auto font-mono">{visibleLeads.length} lead{visibleLeads.length === 1 ? "" : "s"}</span>
          </CardContent>
        </Card>
      )}

      {/* Leads table — hidden on HubSpot Queue tab (queue renders its own list) */}
      {tab !== "hubspot" && (
      <Card className="border-slate-200">
        <div className="p-3 border-b border-slate-200 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 max-w-sm">
            <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="text"
              data-testid="leads-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, company, job title…"
              className="w-full bg-white border border-slate-200 rounded-md pl-8 pr-7 py-1.5 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {search && (
              <button
                type="button"
                data-testid="leads-search-clear"
                onClick={() => setSearch("")}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-slate-400 hover:text-slate-700"
                aria-label="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <span className="text-xs text-slate-500 ml-auto font-mono">{visibleLeads.length} lead{visibleLeads.length === 1 ? "" : "s"}</span>
        </div>
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="p-8 text-sm text-slate-400 font-mono">Loading…</div>
          ) : visibleLeads.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500">
              {search
                ? "No leads match your search — try a different term or clear the search."
                : tab === "converted"
                ? "No converted leads yet — convert a lead from the Active tab and it will move here."
                : tab === "nurture"
                ? "No nurtured leads yet — park an Active lead with the Nurture button to come back to it later."
                : <>No leads match these filters. <button className="text-blue-700 underline" onClick={() => setShowCreate(true)}>Add the first one</button>.</>}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-left text-[11px] font-semibold text-slate-500 uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Company</th>
                  <th className="px-4 py-3">Industry</th>
                  <th className="px-4 py-3">STAX</th>
                  <th className="px-4 py-3 text-center">Users</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Owner</th>
                  <th className="px-4 py-3">{tab === "converted" ? "Converted" : tab === "nurture" ? "Nurtured" : "Next action"}</th>
                  <th className="px-4 py-3">Created</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody data-testid="leads-table-body">
                {visibleLeads.map((l) => {
                  const meta = STATUS_META[l.status] || STATUS_META.Unqualified;
                  const openEdit = () => {
                    if (l.status === "Dead" || l.status === "Nurture") return;
                    setEditing(l);
                  };
                  return (
                    <tr
                      key={l.id}
                      onClick={openEdit}
                      className={`border-b border-slate-100 hover:bg-slate-50 ${l.status === "Dead" || l.status === "Nurture" ? "" : "cursor-pointer"}`}
                      data-testid={`lead-row-${l.id}`}
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900">{l.first_name} {l.last_name}</div>
                        <div className="text-xs text-slate-500">{l.job_title || "—"}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-700 font-medium">{l.company}</td>
                      <td className="px-4 py-3 text-xs text-slate-600">{l.industry || "—"}</td>
                      <td className="px-4 py-3">
                        {Array.isArray(l.interested_products) && l.interested_products.length > 0 ? (
                          <div className="flex flex-wrap gap-1 max-w-[220px]" data-testid={`lead-stax-${l.id}`} title={l.interested_products.join(" · ")}>
                            {l.interested_products.slice(0, 3).map((p) => (
                              <span key={p} className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded border border-blue-200 bg-blue-50 text-blue-800">
                                <svg className="h-2.5 w-2.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M16.704 5.29a1 1 0 010 1.42l-7.5 7.5a1 1 0 01-1.42 0l-3.5-3.5a1 1 0 111.42-1.42L8.5 12.08l6.79-6.79a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                                <span className="truncate max-w-[90px]">{p}</span>
                              </span>
                            ))}
                            {l.interested_products.length > 3 && (
                              <span className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 text-slate-600" title={l.interested_products.slice(3).join(" · ")}>
                                +{l.interested_products.length - 3}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center text-sm text-slate-700">{l.number_of_users ?? "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded text-[11px] font-mono border ${meta.color}`}>{l.status}</span>
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-600">{l.owner_name || "—"}</td>
                      <td className="px-4 py-3 text-xs text-slate-600">
                        {tab === "converted" ? (
                          <div className="font-mono text-slate-500">{l.converted_at ? fmtDate(l.converted_at) : "—"}</div>
                        ) : tab === "nurture" ? (
                          <div className="font-mono text-slate-500">{l.nurtured_at ? fmtDate(l.nurtured_at) : "—"}</div>
                        ) : l.next_action ? (
                          <div>
                            <div>{l.next_action}</div>
                            <div className="text-slate-400 font-mono">{l.next_action_date || "—"}</div>
                          </div>
                        ) : "—"}
                      </td>
                      <td className="px-4 py-3 text-xs text-slate-500 font-mono">{fmtDate(l.created_at)}</td>
                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1 justify-end">
                          {tab === "dead" ? (
                            <div className="flex items-center gap-2">
                              {l.kill_reason_category && <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 border border-rose-200 whitespace-nowrap">{l.kill_reason_category}</span>}
                              {l.kill_reason && <span className="text-[11px] text-rose-700 italic max-w-[200px] truncate" title={l.kill_reason}>“{l.kill_reason}”</span>}
                              <Button size="sm" variant="outline" onClick={() => reviveLead(l)} data-testid={`revive-${l.id}`}>
                                Revive
                              </Button>
                            </div>
                          ) : tab === "nurture" ? (
                            <div className="flex items-center gap-2">
                              {l.nurture_followup_date && (
                                <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200 whitespace-nowrap" title="Follow up on">
                                  <CalendarIcon className="h-2.5 w-2.5 inline mr-1" />{l.nurture_followup_date}
                                </span>
                              )}
                              {l.nurture_reason && <span className="text-[11px] text-amber-700 italic max-w-[200px] truncate" title={l.nurture_reason}>“{l.nurture_reason}”</span>}
                              <Button size="sm" variant="outline" onClick={() => reviveLead(l)} data-testid={`revive-${l.id}`}>
                                Revive
                              </Button>
                            </div>
                          ) : l.status !== "Converted" ? (
                            <>
                              <Button variant="ghost" size="icon" onClick={() => setEditing(l)} data-testid={`edit-${l.id}`}>
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => setConverting(l)} data-testid={`convert-${l.id}`}>
                                Convert <ArrowRight className="h-3 w-3 ml-1" />
                              </Button>
                              <Button size="sm" variant="ghost" className="text-amber-700 hover:text-amber-800 hover:bg-amber-50" onClick={() => setNurturing(l)} data-testid={`nurture-${l.id}`}>
                                <Coffee className="h-3.5 w-3.5 mr-1" /> Nurture
                              </Button>
                              <Button size="sm" variant="ghost" className="text-rose-600 hover:text-rose-700 hover:bg-rose-50" onClick={() => setKilling(l)} data-testid={`kill-${l.id}`}>
                                <XCircle className="h-3.5 w-3.5 mr-1" /> Kill
                              </Button>
                            </>
                          ) : (
                            <ConvertedLink lead={l} />
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
      )}

      {/* Secondary dashboard panels */}
      {dashboard && (
        <div className="grid lg:grid-cols-2 gap-5">
          <SimpleListCard
            title={`Top 10 leads · ${PERIODS.find((p) => p.v === period)?.l}`}
            empty="No leads in this window"
            items={dashboard.top_leads}
            render={(l) => (
              <div className="flex items-center justify-between text-sm">
                <div>
                  <span className="font-medium text-slate-900">{l.company}</span>
                  <span className="text-slate-400 ml-2 text-xs">{l.first_name} {l.last_name}</span>
                </div>
                <div className="text-xs text-slate-500 font-mono">{l.number_of_users ?? "—"} users · {l.status}</div>
              </div>
            )}
          />
          <SimpleListCard
            title="Stale leads (>3 months, not converted)"
            empty="No stale leads"
            items={dashboard.stale_leads}
            render={(l) => (
              <div className="flex items-center justify-between text-sm">
                <div>
                  <span className="font-medium text-slate-900">{l.company}</span>
                  <span className="text-slate-400 ml-2 text-xs">{l.first_name} {l.last_name}</span>
                </div>
                <div className="text-xs text-amber-700 font-mono">{fmtDate(l.created_at)}</div>
              </div>
            )}
          />
          <SimpleListCard
            title={`Upcoming next-actions · ${PERIODS.find((p) => p.v === period)?.l}`}
            empty="No actions scheduled"
            items={dashboard.timeline}
            render={(l) => (
              <div className="flex items-center justify-between text-sm">
                <div>
                  <span className="font-medium text-slate-900">{l.company}</span>
                  <span className="text-slate-400 ml-2 text-xs">{l.next_action}</span>
                </div>
                <div className="text-xs text-slate-500 font-mono">{l.next_action_date}</div>
              </div>
            )}
          />
          <SimpleListCard
            title="Recently converted"
            empty="None yet in this period"
            items={dashboard.recent_converted}
            render={(l) => (
              <div className="flex items-center justify-between text-sm">
                <div>
                  <span className="font-medium text-slate-900">{l.company}</span>
                  <span className="text-slate-400 ml-2 text-xs">{l.first_name} {l.last_name}</span>
                </div>
                <ConvertedLink lead={l} small />
              </div>
            )}
          />
        </div>
      )}

      <LeadsActivityCalendar users={users} />

      {showCreate && (
        <LeadDialog
          lead={null}
          meta={meta}
          users={users}
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); load(); }}
        />
      )}
      {editing && (
        <LeadDialog
          lead={editing}
          meta={meta}
          users={users}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
          onDeleted={() => { setEditing(null); load(); }}
        />
      )}
      {converting && (
        <ConvertDialog
          lead={converting}
          onClose={() => setConverting(null)}
          onConverted={() => { setConverting(null); load(); }}
        />
      )}
      {killing && (
        <KillLeadDialog
          lead={killing}
          onClose={() => setKilling(null)}
          onKilled={() => { setKilling(null); load(); }}
        />
      )}
      {nurturing && (
        <NurtureLeadDialog
          lead={nurturing}
          onClose={() => setNurturing(null)}
          onNurtured={() => { setNurturing(null); load(); }}
        />
      )}
      {showBulkImport && (
        <BulkImportDialog
          onClose={() => setShowBulkImport(false)}
          onDone={() => { setShowBulkImport(false); load(); }}
        />
      )}
    </div>
  );
}

const KpiTile = ({ label, value, icon: Icon, accent, hint }) => {
  const colors = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
    blue: "border-blue-200 bg-blue-50 text-blue-800",
    violet: "border-violet-200 bg-violet-50 text-violet-800",
    amber: "border-amber-200 bg-amber-50 text-amber-800",
    orange: "border-orange-200 bg-orange-50 text-orange-800",
  };
  return (
    <Card className={`border ${colors[accent]?.split(" ")[0] || "border-slate-200"}`} title={hint || undefined}>
      <CardContent className="p-4 flex items-start justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{label}</div>
          <div className="font-heading text-3xl font-bold text-slate-900 mt-1">{value}</div>
          {hint && <div className="text-[10px] text-slate-500 mt-1 leading-tight max-w-[180px]">{hint}</div>}
        </div>
        <div className={`p-2 rounded ${colors[accent] || ""}`}>
          <Icon className="h-4 w-4" />
        </div>
      </CardContent>
    </Card>
  );
};

const SimpleListCard = ({ title, items, empty, render, initialLimit = 10 }) => {
  const [expanded, setExpanded] = useState(false);
  const list = items || [];
  const shown = expanded ? list : list.slice(0, initialLimit);
  const hidden = Math.max(0, list.length - shown.length);
  const testId = `report-card-${(title || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`;
  return (
    <Card className="border-slate-200" data-testid={testId}>
      <CardHeader className="border-b border-slate-100 py-3">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-sm font-heading">{title}</CardTitle>
          {list.length > 0 && (
            <span className="text-[11px] font-mono text-slate-400">
              {expanded ? `${list.length} shown` : `Top ${Math.min(initialLimit, list.length)} of ${list.length}`}
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {list.length === 0 ? (
          <div className="p-5 text-xs text-slate-400 italic">{empty}</div>
        ) : (
          <>
            <ul className="divide-y divide-slate-100">
              {shown.map((it) => <li key={it.id} className="px-4 py-2.5">{render(it)}</li>)}
            </ul>
            {hidden > 0 && !expanded && (
              <button
                type="button"
                onClick={() => setExpanded(true)}
                className="w-full px-4 py-2 text-xs font-medium text-blue-700 hover:bg-blue-50 border-t border-slate-100 flex items-center justify-center gap-1"
                data-testid={`${testId}-show-all`}
              >
                Show all {list.length} <ChevronDown className="h-3 w-3" />
              </button>
            )}
            {expanded && list.length > initialLimit && (
              <button
                type="button"
                onClick={() => setExpanded(false)}
                className="w-full px-4 py-2 text-xs font-medium text-slate-500 hover:bg-slate-50 border-t border-slate-100 flex items-center justify-center gap-1"
                data-testid={`${testId}-show-less`}
              >
                Show top {initialLimit} <ChevronUp className="h-3 w-3" />
              </button>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

function ConvertedLink({ lead, small }) {
  const nav = useNavigate();
  if (!lead.converted_account_id) {
    return <span className="text-[11px] text-emerald-700 font-mono">Converted</span>;
  }
  return (
    <button
      type="button"
      onClick={() => nav(`/app/accounts/${lead.converted_account_id}`)}
      className={`text-emerald-700 hover:text-emerald-900 underline font-mono ${small ? "text-[11px]" : "text-xs"}`}
    >
      View account
    </button>
  );
}

/* ----- Create / edit dialog ----- */
function LeadDialog({ lead, meta, users, onClose, onSaved, onDeleted }) {
  const isEdit = !!lead;
  const [f, setF] = useState({
    owner_id: lead?.owner_id || "",
    first_name: lead?.first_name || "",
    last_name: lead?.last_name || "",
    company: lead?.company || "",
    job_title: lead?.job_title || "",
    email: lead?.email || "",
    phone: lead?.phone || "",
    lead_source: lead?.lead_source || "",
    number_of_users: lead?.number_of_users || "",
    status: lead?.status || "Unqualified",
    industry: lead?.industry || lead?.interested_service || "",
    next_action: lead?.next_action || "",
    next_action_date: lead?.next_action_date || "",
    notes: lead?.notes || "",
    interested_products: lead?.interested_products || [],
    linked_account_id: lead?.linked_account_id || "",
  });
  const [busy, setBusy] = useState(false);

  const setField = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!f.first_name || !f.last_name || (!f.company && !f.linked_account_id)) {
      toast.error("First name, last name and a company (or linked account) are required");
      return;
    }
    const payload = {
      ...f,
      number_of_users: f.number_of_users === "" ? null : Number(f.number_of_users),
      owner_id: f.owner_id || null,
    };
    // Strip empty optional strings
    Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
    setBusy(true);
    try {
      if (isEdit) {
        await api.patch(`/leads/${lead.id}`, payload);
        toast.success("Lead updated");
      } else {
        await api.post("/leads", payload);
        toast.success("Lead created");
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await api.delete(`/leads/${lead.id}`);
      toast.success("Lead deleted");
      onDeleted();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit lead — ${lead.first_name} ${lead.last_name}` : "New lead"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <FieldInput label="First name *" value={f.first_name} onChange={(v) => setField("first_name", v)} testid="lead-first" />
            <FieldInput label="Last name *" value={f.last_name} onChange={(v) => setField("last_name", v)} testid="lead-last" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">Link to existing account (optional)</label>
            <div className="mt-1">
              <AccountPicker
                value={f.linked_account_id}
                onChange={(id, name) => setF((p) => ({ ...p, linked_account_id: id, company: name || p.company }))}
                testid="lead-account-picker"
              />
            </div>
            <p className="mt-1 text-[11px] text-slate-400">
              {f.linked_account_id ? "Linked to a current account — company is set from it." : "Leave empty for a new prospect and type the company below."}
            </p>
          </div>
          <FieldInput label={f.linked_account_id ? "Company (from linked account)" : "Company *"} value={f.company} onChange={(v) => setField("company", v)} testid="lead-company" />
          <div className="grid grid-cols-2 gap-3">
            <FieldInput label="Job title" value={f.job_title} onChange={(v) => setField("job_title", v)} />
            <FieldSelect
              label="Lead source"
              value={f.lead_source || ""}
              onChange={(v) => setField("lead_source", v)}
              options={[
                { v: "", l: "—" },
                ...((meta.lead_sources && meta.lead_sources.length ? meta.lead_sources : []).map((s) => ({ v: s, l: s }))),
                // Keep legacy free-text values selectable so they don't silently disappear
                ...(f.lead_source && !(meta.lead_sources || []).includes(f.lead_source)
                  ? [{ v: f.lead_source, l: `${f.lead_source} (legacy)` }]
                  : []),
              ]}
              testid="lead-source-select"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <FieldInput label="Email" value={f.email} onChange={(v) => setField("email", v)} testid="lead-email" />
            <FieldInput label="Phone" value={f.phone} onChange={(v) => setField("phone", v)} testid="lead-phone" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <FieldInput label="Number of users" type="number" value={f.number_of_users} onChange={(v) => setField("number_of_users", v)} testid="lead-users" />
            <FieldSelect label="Status" value={f.status} onChange={(v) => setField("status", v)} options={meta.statuses.filter((s) => s !== "Converted").map((s) => ({ v: s, l: s }))} />
          </div>
          <FieldSelect
            label="Industry"
            value={f.industry}
            onChange={(v) => setField("industry", v)}
            options={[{ v: "", l: "—" }, ...(meta.industries || meta.interested_services || []).map((s) => ({ v: s, l: s }))]}
          />
          <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">STAX products of interest</label>
            <div className="mt-1.5">
              <ProductCheckboxes
                products={meta.products || []}
                selected={f.interested_products}
                onChange={(arr) => setField("interested_products", arr)}
                testidPrefix="lead-stax"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <FieldSelect
              label="Next action"
              value={f.next_action}
              onChange={(v) => setField("next_action", v)}
              options={[{ v: "", l: "—" }, ...meta.next_actions.map((s) => ({ v: s, l: s }))]}
            />
            <FieldInput label="Action date" type="date" value={f.next_action_date} onChange={(v) => setField("next_action_date", v)} />
          </div>
          <FieldSelect
            label="Owner"
            value={f.owner_id || "__me__"}
            onChange={(v) => setField("owner_id", v === "__me__" ? "" : v)}
            options={[{ v: "__me__", l: "(Default to me)" }, ...users.map((u) => ({ v: u.user_id, l: u.name }))]}
          />
          <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Notes</label>
            <Textarea value={f.notes} onChange={(e) => setField("notes", e.target.value)} rows={3} />
          </div>
        </div>
        <DialogFooter className="flex flex-wrap gap-2 justify-between">
          <div>
            {isEdit && (
              <Button variant="outline" onClick={remove} className="text-red-700 hover:text-red-900">
                <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={save} disabled={busy} data-testid="lead-save-btn">
              {busy ? "Saving…" : isEdit ? "Save changes" : "Create lead"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ----- Convert dialog ----- */
function KillLeadDialog({ lead, onClose, onKilled }) {
  const [reason, setReason] = useState("");
  const [category, setCategory] = useState("");
  const [busy, setBusy] = useState(false);

  const kill = async () => {
    setBusy(true);
    try {
      await api.post(`/leads/${lead.id}/kill`, {
        reason: reason || undefined,
        reason_category: category || undefined,
      });
      toast.success("Lead marked as Dead");
      onKilled();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to kill lead");
      setBusy(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="kill-lead-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700">
            <XCircle className="h-5 w-5" /> Kill lead
          </DialogTitle>
          <DialogDescription>
            Mark <b>{lead.first_name} {lead.last_name}</b>{lead.company ? ` at ${lead.company}` : ""} as <b>Dead</b> —
            we couldn't convert it. It's kept for tracking under the Dead tab and can be revived later.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Reason category (optional)</label>
            <Select value={category || "__none__"} onValueChange={(v) => setCategory(v === "__none__" ? "" : v)}>
              <SelectTrigger data-testid="kill-reason-category"><SelectValue placeholder="— Select a reason —" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">— None —</SelectItem>
                {["Staying with Incumbent", "Technical Challenge", "Budget", "Non Attendance"].map((r) => (
                  <SelectItem key={r} value={r}>{r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Notes (optional)</label>
            <Textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. No budget this year · went with a competitor · unresponsive after 5 attempts"
              data-testid="kill-reason"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={kill} disabled={busy} className="bg-rose-600 hover:bg-rose-700" data-testid="kill-confirm">
            {busy ? "Killing…" : "Mark as Dead"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function NurtureLeadDialog({ lead, onClose, onNurtured }) {
  const [reason, setReason] = useState("");
  const [followup, setFollowup] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/leads/${lead.id}/nurture`, {
        reason: reason || undefined,
        followup_date: followup || undefined,
      });
      toast.success("Lead parked in Nurture");
      onNurtured();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to nurture lead");
      setBusy(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="nurture-lead-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-700">
            <Coffee className="h-5 w-5" /> Nurture lead
          </DialogTitle>
          <DialogDescription>
            Park <b>{lead.first_name} {lead.last_name}</b>{lead.company ? ` at ${lead.company}` : ""} in <b>Nurture</b> —
            not ready to convert, but worth re-engaging later. Note a reason and when to come back to it.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Follow-up date (optional)</label>
            <Input
              type="date"
              value={followup}
              onChange={(e) => setFollowup(e.target.value)}
              data-testid="nurture-followup"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Reason / notes (optional)</label>
            <Textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. No budget this quarter · waiting on renewal · needs tech validation"
              data-testid="nurture-reason"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="bg-amber-600 hover:bg-amber-700" data-testid="nurture-confirm">
            {busy ? "Saving…" : "Move to Nurture"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}



function ConvertDialog({ lead, onClose, onConverted }) {
  // The convert dialog became the "proper" new-deal moment, so it carries
  // enough fields to pass the deal into forecasting straight away.
  //   • dealType is mandatory (New Business vs. Renewals)
  //   • pickedIds references the STAX solutions catalogue — the backend
  //     resolves them into a product_items manifest on the deal
  //   • dealName is auto-suggested from CUSTOMER — SOLUTIONS — TERM but the
  //     engineer can override anything they want.
  const [catalog, setCatalog] = useState([]);
  const [pickedIds, setPickedIds] = useState([]);
  const [dealType, setDealType] = useState("New Business");
  const [amount, setAmount] = useState("");
  const [mrr, setMrr] = useState("");
  const [term, setTerm] = useState(12);
  const [nameEdited, setNameEdited] = useState(false);
  const [dealName, setDealName] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  // Load the STAX solutions catalogue once; expose a category-grouped structure.
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/stax-products");
        setCatalog(Array.isArray(r.data) ? r.data : []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not load STAX solutions");
      }
    })();
  }, []);

  // Group solutions by category, preserving the API's sort order.
  const groupedCatalog = useMemo(() => {
    const g = {};
    catalog.forEach((p) => {
      const key = p.category || "Other";
      (g[key] = g[key] || []).push(p);
    });
    return g;
  }, [catalog]);

  // Suggested name: "ACME — Boxphish, SentinelOne — 12m". Keep the engineer's
  // edit if they typed something custom.
  const pickedProductNames = useMemo(
    () => pickedIds.map((id) => catalog.find((p) => p.id === id)?.name).filter(Boolean),
    [pickedIds, catalog],
  );
  const suggestedName = useMemo(() => {
    const products = pickedProductNames.join(", ");
    const t = Number(term) || 12;
    if (products) return `${lead.company} — ${products} — ${t}m`;
    return `${lead.company} — ${(lead.industry || lead.interested_service) || "New opportunity"}`;
  }, [lead.company, lead.industry, lead.interested_service, pickedProductNames, term]);
  // Keep the field in sync with the suggestion UNTIL the engineer edits it.
  useEffect(() => {
    if (!nameEdited) setDealName(suggestedName);
  }, [suggestedName, nameEdited]);

  const toggleProduct = (id) => {
    setPickedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const convert = async () => {
    if (!dealType) { toast.error("Choose New Business or Renewals"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/leads/${lead.id}/convert`, {
        deal_name: dealName,
        deal_amount: Number(amount) || 0,
        deal_mrr: mrr === "" ? null : Number(mrr),
        deal_term_months: Number(term) || 12,
        deal_type: dealType,
        product_ids: pickedIds,
      });
      toast.success("Lead converted — account + live deal created");
      onConverted();
      nav(`/app/accounts/${r.data.account_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Convert failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="convert-dialog">
        <DialogHeader>
          <DialogTitle>Convert lead to deal</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2 text-sm">
          <div className="bg-slate-50 border border-slate-200 rounded p-3 text-xs">
            <div><span className="text-slate-500">Lead:</span> <strong>{lead.first_name} {lead.last_name}</strong> at <strong>{lead.company}</strong></div>
            {(lead.industry || lead.interested_service) && <div className="mt-1 text-slate-500">Industry: {lead.industry || lead.interested_service}</div>}
          </div>

          {/* Deal type — mandatory */}
          <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-slate-500 flex items-center gap-1">
              Deal type <span className="text-rose-600">*</span>
            </label>
            <div className="mt-1 grid grid-cols-2 gap-2">
              {["New Business", "Renewals"].map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setDealType(t)}
                  data-testid={`convert-deal-type-${t.replace(/\s+/g, "-").toLowerCase()}`}
                  className={`rounded-md border text-sm px-3 py-2 transition-colors ${
                    dealType === t
                      ? "bg-emerald-50 border-emerald-500 text-emerald-900 font-medium"
                      : "bg-white border-slate-200 text-slate-700 hover:border-slate-300"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          {/* STAX solutions — multi-select, grouped by category */}
          <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-slate-500 flex items-center justify-between">
              <span>STAX solutions to include</span>
              {pickedIds.length > 0 && (
                <button type="button" onClick={() => setPickedIds([])}
                        className="text-[10px] text-rose-600 hover:underline">
                  Clear
                </button>
              )}
            </label>
            {catalog.length === 0 ? (
              <div className="text-xs text-slate-400 mt-1">
                No STAX solutions yet — add some under Admin → STAX to tick them here.
              </div>
            ) : (
              <div className="mt-1 space-y-2 max-h-56 overflow-y-auto border rounded p-2 bg-white"
                   data-testid="convert-product-picker">
                {Object.entries(groupedCatalog).map(([category, prods]) => (
                  <div key={category}>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mb-1">{category}</div>
                    <div className="grid grid-cols-2 gap-1.5">
                      {prods.map((p) => {
                        const checked = pickedIds.includes(p.id);
                        return (
                          <label
                            key={p.id}
                            data-testid={`convert-product-${p.id}`}
                            className={`flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-xs cursor-pointer transition-colors ${
                              checked ? "bg-blue-50 border-blue-300 text-blue-900" : "bg-white border-slate-200 hover:border-slate-300 text-slate-700"
                            }`}
                          >
                            <input
                              type="checkbox"
                              className="h-3.5 w-3.5 accent-blue-600"
                              checked={checked}
                              onChange={() => toggleProduct(p.id)}
                            />
                            <span className="truncate">{p.name}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Deal name with naming-convention hint */}
          <div>
            <label className="text-[10px] font-mono uppercase tracking-wider text-slate-500 flex items-center gap-1">
              Deal name <span className="text-rose-600">*</span>
            </label>
            <Input
              value={dealName}
              onChange={(e) => { setNameEdited(true); setDealName(e.target.value); }}
              className="mt-1"
              data-testid="convert-deal-name"
            />
            <div className="text-[11px] text-slate-500 mt-1 italic">
              Convention: <code className="bg-slate-100 px-1 rounded">CUSTOMER — PRODUCT — TERM</code>
              {!nameEdited && pickedIds.length > 0 && (
                <> (auto-filled from your picks — edit if you need to)</>
              )}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <FieldInput label="Amount £" type="number" value={amount} onChange={setAmount} testid="convert-amount" />
            <FieldInput label="MRR £" type="number" value={mrr} onChange={setMrr} testid="convert-mrr" />
            <FieldInput label="Term (months)" type="number" value={term} onChange={(v) => setTerm(v)} testid="convert-term" />
          </div>

          <div className="text-xs text-slate-500 bg-blue-50 border border-blue-100 rounded p-2">
            Converting will create the <strong>{lead.company}</strong> account (or reuse it if it exists)
            and a new <strong>{dealType || "—"}</strong> deal at the <strong>Webinar / Introduction</strong> stage.
            The lead's contact details are attached to the new account.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={convert} disabled={busy || !dealName || !dealType} data-testid="convert-confirm-btn">
            {busy ? "Converting…" : "Create account + deal"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const FieldInput = ({ label, value, onChange, type = "text", placeholder, testid }) => (
  <div>
    <label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{label}</label>
    <Input
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="mt-1"
      data-testid={testid}
    />
  </div>
);

const FieldSelect = ({ label, value, onChange, options, testid }) => (
  <div>
    <label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{label}</label>
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger className="mt-1" data-testid={testid}><SelectValue placeholder="—" /></SelectTrigger>
      <SelectContent>
        {options.map((o) => <SelectItem key={o.v || "_"} value={o.v || "_blank_"}>{o.l}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);


function BulkImportDialog({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = async () => {
    try {
      const r = await api.get("/admin/import/leads/template", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "leads_template.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not download template");
    }
  };

  const submit = async () => {
    if (!file) { toast.error("Choose a CSV file first"); return; }
    setUploading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/import/leads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      toast.success(`Import complete: ${r.data.created} created, ${r.data.updated} updated${r.data.skipped ? `, ${r.data.skipped} skipped` : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="leads-bulk-import-dialog">
        <DialogHeader>
          <DialogTitle>Bulk import leads</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2 text-sm">
          <p className="text-slate-600">
            Upload a CSV with these headers: <span className="font-mono text-xs text-slate-800">first_name, last_name, company, job_title, email, phone, lead_source, number_of_users, status, industry, next_action, next_action_date, owner_name, notes</span>.
          </p>
          <p className="text-xs text-slate-500">
            Dupe check on email (case-insensitive). If none, checks first + last + company. Existing matches are updated; new rows are inserted.
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="download-leads-template-btn">
              Download template
            </Button>
          </div>
          <input
            type="file"
            accept=".csv"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="block w-full text-sm border border-slate-200 rounded p-2 file:mr-2 file:border-0 file:bg-slate-100 file:text-slate-700 file:font-medium file:rounded file:px-2 file:py-1"
            data-testid="leads-import-file-input"
          />
          {result && (
            <div className="rounded border border-slate-200 bg-slate-50 p-3 space-y-1 text-xs" data-testid="leads-import-result">
              <div><span className="font-mono text-slate-500">Received:</span> {result.received}</div>
              <div><span className="font-mono text-emerald-700">Created:</span> {result.created}</div>
              <div><span className="font-mono text-blue-700">Updated:</span> {result.updated}</div>
              {result.skipped > 0 && <div><span className="font-mono text-amber-700">Skipped:</span> {result.skipped}</div>}
              {result.errors && result.errors.length > 0 && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-red-700 text-xs">First {result.errors.length} error{result.errors.length === 1 ? "" : "s"}</summary>
                  <ul className="mt-1 space-y-0.5 ml-3 list-disc">
                    {result.errors.map((e, i) => <li key={i} className="text-slate-600">Row {e.row}: {e.error}</li>)}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="leads-import-cancel-btn">
            {result ? "Close" : "Cancel"}
          </Button>
          {!result && (
            <Button onClick={submit} disabled={!file || uploading} data-testid="leads-import-submit-btn">
              <Upload className="h-4 w-4 mr-1.5" /> {uploading ? "Importing…" : "Import"}
            </Button>
          )}
          {result && (
            <Button onClick={onDone} data-testid="leads-import-done-btn">Done</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
